import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { submissionsAPI, usersAPI, challengesAPI } from '../../services/api'
import { 
  TrophyIcon, 
  DocumentTextIcon, 
  ChartBarIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  FireIcon,
  StarIcon,
  UserGroupIcon,
  CogIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import ImageWithFallback from '../../components/ui/ImageWithFallback'
import { getCategoryGradient, getPlaceholderImage, getCategoryImage, heroImages, getLocalImage } from '../../utils/images'
import { format } from 'date-fns'

const statusIcons = {
  submitted: ClockIcon,
  under_review: ExclamationTriangleIcon,
  evaluated: CheckCircleIcon,
}

const statusColors = {
  submitted: 'text-yellow-600 bg-yellow-100',
  under_review: 'text-blue-600 bg-blue-100',
  evaluated: 'text-green-600 bg-green-100',
}

export default function DashboardPage() {
  const { user } = useAuth()

  // User stats
  const { data: userStats, isLoading: statsLoading } = useQuery(
    ['user-stats', user?.id],
    () => usersAPI.getStats(user.id),
    {
      select: (response) => response.data,
      enabled: !!user?.id,
      retry: false, // Don't retry on failure
      onError: (error) => {
        console.log('User stats fetch failed (this is normal for admin users):', error)
      }
    }
  )

  // Recent submissions
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery(
    'my-submissions',
    () => submissionsAPI.getMySubmissions({ limit: 5 }),
    {
      select: (response) => response.data.submissions,
      retry: false,
      onError: (error) => {
        console.log('Submissions fetch failed (this is normal for admin users):', error)
      }
    }
  )

  // Active challenges
  const { data: challengesData, isLoading: challengesLoading } = useQuery(
    'active-challenges',
    () => challengesAPI.getAll({ status: 'active' }),
    {
      select: (response) => response.data.challenges.slice(0, 3)
    }
  )

  // User achievements
  const { data: achievementsData } = useQuery(
    ['user-achievements', user?.id],
    () => usersAPI.getAchievements(user.id),
    {
      select: (response) => response.data,
      enabled: !!user?.id,
      retry: false,
      onError: (error) => {
        console.log('Achievements fetch failed (this is normal for admin users):', error)
      }
    }
  )

  if (statsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-primary-600 to-secondary-600 overflow-hidden">
        <div className="absolute inset-0">
          <ImageWithFallback
            src={heroImages.dashboard}
            alt="Dashboard background"
            className="w-full h-full object-cover opacity-20"
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 300'%3E%3Crect width='1200' height='300' fill='%233b82f6'/%3E%3C/svg%3E"
          />
        </div>
        <div className="relative max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center mb-4">
                <ImageWithFallback
                  src={getLocalImage('experts')}
                  alt={user?.firstName}
                  className="h-16 w-16 rounded-full object-cover border-4 border-white shadow-lg"
                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /%3E%3C/svg%3E"
                />
                <div className="ml-4">
                  <h1 className="text-3xl font-bold text-white sm:text-4xl">
                    Welcome back, {user?.firstName}!
                  </h1>
                  <p className="mt-2 text-lg text-primary-100">
                    Here's what's happening with your AI challenge journey
                  </p>
                </div>
              </div>
              {userStats && (
                <div className="flex flex-wrap gap-6 mt-6">
                  <div className="flex items-center text-white">
                    <TrophyIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">{userStats.totalSubmissions || 0} Submissions</span>
                  </div>
                  <div className="flex items-center text-white">
                    <StarIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">Rank #{userStats.globalRank || 'N/A'}</span>
                  </div>
                  <div className="flex items-center text-white">
                    <FireIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">{userStats.averageScore || 0} Avg Score</span>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
              <Link
                to="/challenges"
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 transition-colors duration-200"
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Join New Challenge
              </Link>
              <Link
                to="/submissions"
                className="inline-flex items-center px-4 py-2 border border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-primary-600 transition-colors duration-200"
              >
                View All Submissions
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Submissions
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {userStats?.statistics.totalSubmissions || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Average Score
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {userStats?.statistics.averageScore || 'N/A'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Best Score
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {userStats?.statistics.bestScore || 'N/A'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Challenges Joined
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {userStats?.statistics.challengesParticipated || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Submissions */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Recent Submissions
                </h3>
                <Link
                  to="/submissions"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  View all
                </Link>
              </div>
              
              {submissionsLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : submissionsData?.length > 0 ? (
                <div className="space-y-3">
                  {submissionsData.map((submission) => {
                    const StatusIcon = statusIcons[submission.status]
                    return (
                      <div key={submission.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`p-1 rounded-full ${statusColors[submission.status]}`}>
                            <StatusIcon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {submission.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {submission.challenge_title}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {submission.score && (
                            <p className="text-sm font-medium text-gray-900">
                              {submission.score}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 capitalize">
                            {submission.status.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by joining a challenge and submitting your solution.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/challenges"
                      className="btn-primary"
                    >
                      Browse Challenges
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Active Challenges */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Active Challenges
                </h3>
                <Link
                  to="/challenges"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  View all
                </Link>
              </div>
              
              {challengesLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : challengesData?.length > 0 ? (
                <div className="space-y-4">
                  {challengesData.map((challenge) => (
                    <div key={challenge.id} className="relative overflow-hidden rounded-lg bg-white border border-gray-200 hover:shadow-md transition-shadow duration-200">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ImageWithFallback
                            src={getCategoryImage(challenge.category)}
                            alt={challenge.category}
                            className="h-20 w-30 object-cover"
                            fallbackSrc={`data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 80'%3E%3Crect width='120' height='80' fill='%23f3f4f6'/%3E%3Ctext x='60' y='40' text-anchor='middle' dy='.3em' fill='%236b7280' font-family='Arial, sans-serif' font-size='10'%3E${challenge.category}%3C/text%3E%3C/svg%3E`}
                          />
                          <div className={`absolute top-2 left-2 w-2 h-2 rounded-full bg-gradient-to-r ${getCategoryGradient(challenge.category)}`} />
                        </div>
                        <div className="flex-1 p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900 line-clamp-1">
                                {challenge.title}
                              </h4>
                              <div className="mt-1 flex items-center space-x-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                  {challenge.category}
                                </span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  challenge.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                                  challenge.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {challenge.difficulty}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-gray-500">
                                Ends {format(new Date(challenge.end_date), 'MMM dd, yyyy')}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <Link
                                to={`/challenges/${challenge.id}`}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-primary-600 bg-primary-100 hover:bg-primary-200 transition-colors duration-200"
                              >
                                View
                              </Link>
                              <Link
                                to={`/challenges/${challenge.id}`}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                              >
                                Join
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No active challenges</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Check back later for new challenges.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Achievements */}
        {achievementsData?.achievements?.length > 0 && (
          <div className="mt-6 bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Recent Achievements
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {achievementsData.achievements.slice(0, 3).map((achievement) => (
                  <div key={achievement.id} className="flex items-center p-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg">
                    <div className="text-2xl mr-3">{achievement.icon}</div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {achievement.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Admin Section */}
        {user?.role === 'admin' && (
          <div className="mt-8">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shadow-lg overflow-hidden">
              <div className="px-6 py-4">
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-lg font-medium text-white">Admin Dashboard</h3>
                </div>
                <p className="mt-1 text-sm text-purple-100">
                  Manage challenges, evaluate submissions, and oversee the platform
                </p>
              </div>
              <div className="bg-white bg-opacity-10 px-6 py-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center p-3 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200 text-white"
                  >
                    <ChartBarIcon className="h-5 w-5 mr-3" />
                    <div>
                      <p className="text-sm font-medium">Overview</p>
                      <p className="text-xs opacity-75">Platform stats</p>
                    </div>
                  </Link>
                  <Link
                    to="/admin/challenges"
                    className="flex items-center p-3 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200 text-white"
                  >
                    <TrophyIcon className="h-5 w-5 mr-3" />
                    <div>
                      <p className="text-sm font-medium">Challenges</p>
                      <p className="text-xs opacity-75">Create & manage</p>
                    </div>
                  </Link>
                  <Link
                    to="/admin/submissions"
                    className="flex items-center p-3 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200 text-white"
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-3" />
                    <div>
                      <p className="text-sm font-medium">Submissions</p>
                      <p className="text-xs opacity-75">Review & evaluate</p>
                    </div>
                  </Link>
                  <Link
                    to="/admin/users"
                    className="flex items-center p-3 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200 text-white"
                  >
                    <UserGroupIcon className="h-5 w-5 mr-3" />
                    <div>
                      <p className="text-sm font-medium">Users</p>
                      <p className="text-xs opacity-75">Manage users</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}